import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Mail, Lock, Globe, Trash2, Save, Palette, Building2, Upload, Check } from 'lucide-react';
import { toast } from 'sonner';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme color options
const THEME_OPTIONS = [
  { id: 'blue', name: 'Blue', primary: '#1e40af', secondary: '#3b82f6', accent: '#dbeafe' },
  { id: 'orange', name: 'Orange', primary: '#c2410c', secondary: '#ea580c', accent: '#fed7aa' },
  { id: 'green', name: 'Green', primary: '#166534', secondary: '#16a34a', accent: '#dcfce7' },
  { id: 'purple', name: 'Purple', primary: '#6b21a8', secondary: '#9333ea', accent: '#f3e8ff' },
  { id: 'charcoal', name: 'Charcoal', primary: '#1f2937', secondary: '#374151', accent: '#e5e7eb' }
];

export default function AccountSettings({ user, onLogout, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const fileInputRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currency: user?.currency || 'USD'
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Brand settings state
  const [brandSettings, setBrandSettings] = useState({
    company_name: '',
    logo: null,
    theme: 'blue',
    tagline: ''
  });
  const [brandLoading, setBrandLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Check if user is an advisor
  const isAdvisor = user?.role === 'advisor';
  const isMobile = useIsMobile();
  
  // Fetch brand settings on mount if advisor
  useEffect(() => {
    if (isAdvisor) {
      fetchBrandSettings();
    }
  }, [isAdvisor]);
  
  const fetchBrandSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/brand-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandSettings(response.data);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      console.error('Error fetching brand settings:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API}/account/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/account/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.'
    );

    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      'This is your last chance. Are you absolutely sure you want to delete your account?'
    );

    if (!doubleConfirm) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/account/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle logo file upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      toast.error('Logo image must be less than 500KB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setLogoPreview(base64);
      setBrandSettings({ ...brandSettings, logo: base64 });
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setBrandSettings({ ...brandSettings, logo: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleBrandSettingsSave = async () => {
    try {
      setBrandLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/advisor/brand-settings`, brandSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Brand settings saved successfully!');
    } catch (error) {
      console.error('Error saving brand settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save brand settings');
    } finally {
      setBrandLoading(false);
    }
  };
  
  // Get selected theme colors
  const selectedTheme = THEME_OPTIONS.find(t => t.id === brandSettings.theme) || THEME_OPTIONS[0];

  return (
    <div data-testid="account-settings-page" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} currentPage="/account-settings" />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-teal-600/[0.06] to-cyan-500/[0.06] blur-3xl" animate={{ x: [0, -20, 0], y: [0, 25, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} style={{ top: '10%', right: '5%' }} />
        </div>
      )}

      <motion.div className="max-w-4xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-teal-500 to-cyan-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
            <h1 data-testid="settings-page-header" className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100">
              <span className="bg-gradient-to-r from-teal-500 to-cyan-400 bg-clip-text text-transparent">Account</span> Settings
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">Manage your account and preferences</p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-white dark:bg-gray-800 p-1 rounded-lg shadow-sm">
            <TabsTrigger 
              value="account" 
              className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900 dark:data-[state=active]:text-blue-300 px-6"
            >
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
            {isAdvisor && (
              <TabsTrigger 
                value="branding" 
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900 dark:data-[state=active]:text-blue-300 px-6"
              >
                <Palette className="w-4 h-4 mr-2" />
                Branding
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            {/* Profile Information */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.1 }}>
            <Card className="p-6 border-gray-200 dark:border-gray-800/60">
              <div className="flex items-center mb-6">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center mr-3">
                  <User className="w-4 h-4 text-teal-500" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Profile Information</h2>
              </div>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Full Name</Label>
                  <Input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Email Address</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Preferred Currency</Label>
                  <Select value={profileData.currency} onValueChange={(value) => setProfileData({ ...profileData, currency: value })}>
                    <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-800">
                      <SelectItem value="USD">US Dollar ($)</SelectItem>
                      <SelectItem value="INR">Indian Rupee (₹)</SelectItem>
                      <SelectItem value="EUR">Euro (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 shadow-lg shadow-teal-600/20">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </Card>
            </motion.div>

            {/* Change Password */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.15 }}>
            <Card className="p-6 border-gray-200 dark:border-gray-800/60">
              <div className="flex items-center mb-6">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center mr-3">
                  <Lock className="w-4 h-4 text-amber-500" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Change Password</h2>
              </div>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Current Password</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Enter current password"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Enter new password"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <Button type="submit" disabled={loading} className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 shadow-lg shadow-amber-600/20">
                  <Lock className="w-4 h-4 mr-2" />
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </Card>
            </motion.div>

            {/* Account Information */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}>
            <Card className="p-6 border-gray-200 dark:border-gray-800/60">
              <div className="flex items-center mb-6">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center mr-3">
                  <Globe className="w-4 h-4 text-blue-500" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Account Information</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Account ID</span>
                  <span className="text-gray-800 dark:text-gray-200 font-mono text-sm">{user?.id?.slice(0, 16)}...</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Member Since</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 dark:text-gray-400">Account Status</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Active</span>
                </div>
              </div>
            </Card>
            </motion.div>

            {/* Danger Zone */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.25 }}>
            <Card className="p-6 border-2 border-rose-200 dark:border-rose-900">
              <div className="flex items-center mb-6">
                <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center mr-3">
                  <Trash2 className="w-4 h-4 text-rose-500" />
                </div>
                <h2 className="text-lg font-semibold text-rose-600 dark:text-rose-400">Danger Zone</h2>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Once you delete your account, there is no going back. All your data including transactions, budgets, goals, and investments will be permanently deleted.
              </p>
              <Button
                onClick={handleDeleteAccount}
                disabled={loading}
                variant="destructive"
                className="bg-rose-600 hover:bg-rose-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </Card>
            </motion.div>
          </TabsContent>
          
          {/* Branding Tab - Only for Advisors */}
          {isAdvisor && (
            <TabsContent value="branding" className="space-y-6">
              {/* Company Information */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.1 }}>
              <Card className="p-6 border-gray-200 dark:border-gray-800/60">
                <div className="flex items-center mb-6">
                  <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center mr-3">
                    <Building2 className="w-4 h-4 text-blue-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Company Information</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="dark:text-gray-300">Company Name</Label>
                    <Input
                      type="text"
                      value={brandSettings.company_name}
                      onChange={(e) => setBrandSettings({ ...brandSettings, company_name: e.target.value })}
                      placeholder="ABC Financial Services"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">This will appear on your PDF financial plans</p>
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Tagline (Optional)</Label>
                    <Input
                      type="text"
                      value={brandSettings.tagline}
                      onChange={(e) => setBrandSettings({ ...brandSettings, tagline: e.target.value })}
                      placeholder="Your Trusted Financial Partner"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                </div>
              </Card>
              </motion.div>
              
              {/* Company Logo */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.15 }}>
              <Card className="p-6 border-gray-200 dark:border-gray-800/60">
                <div className="flex items-center mb-6">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center mr-3">
                    <Upload className="w-4 h-4 text-purple-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Company Logo</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-6">
                    {/* Logo Preview */}
                    <div className="flex-shrink-0">
                      <div 
                        className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-700"
                      >
                        {logoPreview ? (
                          <img 
                            src={logoPreview} 
                            alt="Company logo" 
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <div className="text-center text-gray-400">
                            <Upload className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-xs">No logo</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Upload Controls */}
                    <div className="flex-grow">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoUpload}
                        accept="image/*"
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="mb-2"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </Button>
                      {logoPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={handleRemoveLogo}
                          className="ml-2 text-rose-600 hover:text-rose-700"
                        >
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Recommended: Square image, max 500KB<br />
                        Supported formats: PNG, JPG, JPEG
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
              </motion.div>
              
              {/* Theme Color */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}>
              <Card className="p-6 border-gray-200 dark:border-gray-800/60">
                <div className="flex items-center mb-6">
                  <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center mr-3">
                    <Palette className="w-4 h-4 text-orange-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Theme Color</h2>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Choose a color theme for your PDF financial plans
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
                  {THEME_OPTIONS.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => setBrandSettings({ ...brandSettings, theme: theme.id })}
                      className={`relative p-2 sm:p-4 rounded-lg border-2 transition-all ${
                        brandSettings.theme === theme.id 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="w-full h-10 sm:h-12 rounded-md mb-1 sm:mb-2"
                        style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)` }}
                      />
                      <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 block truncate">{theme.name}</span>
                      {brandSettings.theme === theme.id && (
                        <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-blue-500 rounded-full p-0.5 sm:p-1">
                          <Check className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </Card>
              </motion.div>
              
              {/* Preview */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.25 }}>
              <Card className="p-6 border-gray-200 dark:border-gray-800/60">
                <div className="flex items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">PDF Header Preview</h2>
                </div>
                <div 
                  className="rounded-lg overflow-hidden shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${selectedTheme.primary} 0%, ${selectedTheme.secondary} 100%)` }}
                >
                  <div className="p-6 text-white">
                    {logoPreview && (
                      <div className="mb-3">
                        <img 
                          src={logoPreview} 
                          alt="Logo preview" 
                          className="h-12 object-contain bg-white/10 rounded p-1"
                        />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold">
                      {brandSettings.company_name ? `${brandSettings.company_name} - ` : ''}Comprehensive Financial Plan
                    </h3>
                    <p className="opacity-90">Prepared for Client Name</p>
                    {brandSettings.tagline && (
                      <p className="text-sm opacity-80 mt-1">{brandSettings.tagline}</p>
                    )}
                  </div>
                  <div className="h-1 bg-gradient-to-r from-yellow-400 to-orange-400" />
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  This is how your PDF header will look
                </p>
              </Card>
              </motion.div>
              
              {/* Save Button */}
              <motion.div className="flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <Button 
                  onClick={handleBrandSettingsSave}
                  disabled={brandLoading}
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 px-8 shadow-lg shadow-blue-600/20"
                  data-testid="save-brand-settings-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {brandLoading ? 'Saving...' : 'Save Brand Settings'}
                </Button>
              </motion.div>
            </TabsContent>
          )}
        </Tabs>
      </motion.div>
      
      <Footer />
    </div>
  );
}
